import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, ChevronDown, X, Shield, BarChart3, Megaphone } from "lucide-react";

const STORAGE_KEY = "tdf-cookie-consent";
const REOPEN_EVENT = "tdf:cookie-consent-reopen";

/**
 * UK GDPR / PECR-compliant cookie banner.
 *
 *   • Defaults all non-essential storage to denied via Google Consent Mode v2
 *     (already wired in /public/index.html — runs before GA4 fires).
 *   • Surfaces three equally-prominent choices: Accept all / Reject /
 *     Customise. PECR requires Reject to be no harder than Accept.
 *   • Persists `{accepted_at, analytics, marketing}` to localStorage and
 *     dispatches `gtag('consent','update',…)` so GA4 starts (or stays
 *     suppressed) the moment a choice is made.
 *   • Reopen via the "Cookie settings" footer link — fires the
 *     `tdf:cookie-consent-reopen` window event.
 */
export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  // Open if no choice on record yet.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const saved = raw ? JSON.parse(raw) : null;
      if (!saved || !saved.accepted_at) {
        setOpen(true);
      } else {
        setAnalytics(!!saved.analytics);
        setMarketing(!!saved.marketing);
      }
    } catch { setOpen(true); }
  }, []);

  // Toggle a body class while open so the floating Future Driver CTA
  // and the Charla chat widget hide themselves — they otherwise overlap
  // the banner buttons on the bottom edge of the viewport.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (open) document.body.classList.add("tdf-cookie-open");
    else document.body.classList.remove("tdf-cookie-open");
    return () => document.body.classList.remove("tdf-cookie-open");
  }, [open]);

  // Listen for "open the banner" events (footer link, etc.)
  useEffect(() => {
    const handler = () => { setExpanded(true); setOpen(true); };
    window.addEventListener(REOPEN_EVENT, handler);
    return () => window.removeEventListener(REOPEN_EVENT, handler);
  }, []);

  const persist = (choice) => {
    const payload = { accepted_at: new Date().toISOString(), ...choice };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch { /* ignore */ }
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        analytics_storage: choice.analytics ? "granted" : "denied",
        ad_storage: choice.marketing ? "granted" : "denied",
        ad_user_data: choice.marketing ? "granted" : "denied",
        ad_personalization: choice.marketing ? "granted" : "denied",
      });
    }
    setOpen(false);
  };

  const acceptAll = () => persist({ analytics: true, marketing: true });
  const rejectAll = () => persist({ analytics: false, marketing: false });
  const saveCustom = () => persist({ analytics, marketing });

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[80] pointer-events-none print:hidden" data-testid="cookie-consent-root">
      <div className="max-w-5xl mx-auto m-3 md:m-5 pointer-events-auto">
        <div className="bg-[#0F1B2E] text-white rounded-2xl shadow-2xl border border-white/10 overflow-hidden" role="dialog" aria-labelledby="tdf-cookie-title" data-testid="cookie-consent-banner">
          <div className="p-5 md:p-6">
            <div className="flex items-start gap-4">
              <span className="hidden sm:inline-flex w-10 h-10 items-center justify-center rounded-xl bg-amber-400/15 text-amber-300 shrink-0">
                <Cookie className="w-5 h-5" />
              </span>
              <div className="flex-1 min-w-0">
                <h2 id="tdf-cookie-title" className="text-base md:text-lg font-extrabold mb-1">We use cookies</h2>
                <p className="text-sm text-slate-300 leading-relaxed">
                  Train Driver Foundation uses essential cookies to keep this site working, plus optional analytics cookies (Google Analytics) to understand how visitors use the site. You can accept all, reject non-essential, or pick what you're happy with. See our{" "}
                  <Link to="/privacy-policy" className="underline font-bold text-white hover:text-[#E63B2E]">privacy policy</Link>.
                </p>
              </div>
              <button type="button" onClick={rejectAll} className="text-slate-400 hover:text-white shrink-0" aria-label="Reject and close" data-testid="cookie-consent-x">
                <X className="w-4 h-4" />
              </button>
            </div>

            {expanded && (
              <div className="mt-4 grid sm:grid-cols-3 gap-2.5" data-testid="cookie-consent-categories">
                <CategoryToggle label="Essential" desc="Login, security, basic site function." Icon={Shield} on disabled tone="emerald" />
                <CategoryToggle label="Analytics" desc="Anonymised GA4 — how visitors use the site." Icon={BarChart3} on={analytics} setOn={setAnalytics} tone="cyan" testId="cookie-toggle-analytics" />
                <CategoryToggle label="Marketing" desc="Personalised ads & remarketing pixels." Icon={Megaphone} on={marketing} setOn={setMarketing} tone="amber" testId="cookie-toggle-marketing" />
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
              {!expanded && (
                <button type="button" onClick={() => setExpanded(true)}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-300 hover:text-white px-3 py-2"
                        data-testid="cookie-consent-customise">
                  Customise <ChevronDown className="w-3.5 h-3.5" />
                </button>
              )}
              <button type="button" onClick={rejectAll}
                      className="rounded-full bg-white/10 hover:bg-white/15 text-white font-bold px-5 py-2.5 text-sm border border-white/20"
                      data-testid="cookie-consent-reject">
                Reject non-essential
              </button>
              <button type="button" onClick={expanded ? saveCustom : acceptAll}
                      className="rounded-full bg-[#E63B2E] hover:bg-[#c92f24] text-white font-bold px-5 py-2.5 text-sm"
                      data-testid="cookie-consent-accept">
                {expanded ? "Save my choices" : "Accept all"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CategoryToggle({ label, desc, Icon, on, setOn, disabled, tone, testId }) {
  const tones = {
    emerald: "border-emerald-400/40 bg-emerald-500/10 text-emerald-200",
    cyan: "border-cyan-400/40 bg-cyan-500/10 text-cyan-200",
    amber: "border-amber-400/40 bg-amber-500/10 text-amber-200",
  };
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-3 flex items-start gap-3">
      <span className={`inline-flex w-9 h-9 items-center justify-center rounded-lg border ${tones[tone] || tones.cyan} shrink-0`}>
        <Icon className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-extrabold text-white">{label}</span>
          <button
            type="button"
            role="switch"
            aria-checked={on}
            disabled={disabled}
            onClick={() => !disabled && setOn?.(!on)}
            data-testid={testId}
            className={`relative w-9 h-5 rounded-full transition ${on ? "bg-emerald-500" : "bg-slate-600"} ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
          >
            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition ${on ? "left-[18px]" : "left-0.5"}`} />
          </button>
        </div>
        <p className="text-[11.5px] text-slate-400 mt-0.5 leading-snug">{desc}</p>
      </div>
    </div>
  );
}

/** Helper exported so the footer link can re-open the banner. */
export function openCookieConsent() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(REOPEN_EVENT));
  }
}
